
import { defineComponent } from 'vue';
import { GlobalAlertTypes } from '@momnt-technologies/common-ui/lib/plugins/alerts/types';
import { debounce } from 'lodash';
import TextInput from '~/components/Inputs/Text.vue';
import TransactionsTable from '~/components/Tables/TransactionsTable.vue';
import { Transaction } from '@/interfaces/transactions';
import { getTransactions, getTransactionTypes } from '@/api/transactions';
import MultiselectFilterWithSearch from '@/components/Filters/MultiselectFilterWithSearch.vue';

type FilterQuery = string | string[] | undefined;

let abortController = new AbortController();

export default defineComponent({
  name: 'TransactionsComponent',

  components: {
    TransactionsTable,
    TextInput,
    MultiselectFilterWithSearch
  },

  data: () => ({
    transactions: [] as Transaction[],
    itemsPerPage: 5,
    totalItems: 0,
    currentPage: 1,
    searchTerm: '',
    filterOptions: [] as Array<string>,
    filterQuery: {} as Record<string, FilterQuery>,
    isLoading: false,
    isEmpty: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debouncedOnSearch: null as any
  }),

  computed: {
    loanNumber() {
      return this.$store.getters['User/activeLoanNumber'];
    }
  },

  created() {
    this.debouncedOnSearch = debounce(this.onSearch, 500);
    this.transactionTypes();
    this.search();
  },

  methods: {
    updateFilters(option: string) {
      this.currentPage = 1;
      const transactionType = { transaction_types: option };
      this.filterQuery = { ...this.filterQuery, ...transactionType };
      this.search();
    },

    onSearch(val: string) {
      this.currentPage = 1;
      this.search(val);
    },

    async search(termParam?: string) {
      const term = termParam || this.searchTerm;

      if (this.isLoading) {
        abortController.abort();
        abortController = new AbortController();
      }
      this.isLoading = true;
      const params: Record<string, string> = {
        search: term,
        page: this.currentPage.toString(),
        page_size: this.itemsPerPage.toString(),
      };
      const filterQueryEntries = Object.entries(this.filterQuery);

      const urlParams = new URLSearchParams(params);

      filterQueryEntries.forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => urlParams.append(key, v));
        } else if (value) {
          urlParams.append(key, value as string);
        }
      });

      urlParams.forEach((value, key) => {
        if (!value) urlParams.delete(key);
      });

      try {
        if (!this.loanNumber) {
          this.$alerts.add({
            text: 'Could not find a loan number.',
            type: GlobalAlertTypes.ERROR,
            dismissible: true,
          });
          return;
        }

        const { data } = await getTransactions(abortController, this.loanNumber, urlParams);
        this.transactions = data.results;
        this.totalItems = data.count;
        this.isEmpty = data.count === 0;
        this.isLoading = false;
      } catch (e) {
        if ((e as DOMException).message !== 'canceled') {
          Promise.reject(e);
        }
      }
    },

    async transactionTypes() {
      try {
        const { data } = await getTransactionTypes();
        const types = data.map((val: string, index: number) => ({ value: index, label: val }));
        this.filterOptions = types;
      } catch (e) {
        if ((e as DOMException).message !== 'canceled') {
          Promise.reject(e);
        }
      }
    },

    updateRequestOptions(page: number) {
      this.currentPage = page;

      this.search();
    },
  },

  watch: {
    searchTerm(val: string) {
      this.onSearch(val);
    }
  }
});
